import { useEffect, useMemo } from "react";
import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  EllipsisHorizontalIcon,
} from "@heroicons/react/24/outline";
import {
  EllipsisHorizontalIcon as EllipsisHorizontalIconSolid,
} from "@heroicons/react/24/solid";
import {
  HomeIcon as HomeIconSolid,
  CubeIcon as CubeIconSolid,
  ClipboardDocumentListIcon as ClipboardDocumentListIconSolid,
  ShoppingCartIcon as ShoppingCartIconSolid,
  BellAlertIcon as BellAlertIconSolid,
  ShoppingBagIcon as ShoppingBagIconSolid,
} from "@heroicons/react/24/solid";
import { Menu, MenuButton, MenuItem, MenuItems, Transition } from "@headlessui/react";
import { useAuthStore } from "../../store/authStore";
import { useReorderAlertStore } from "../../store/reorderAlertStore";
import { useOrderStore } from "../../store/orderStore";
import { AlertBadge } from "../molecules/AlertBadge";
import { OrdersBadge } from "../molecules/OrdersBadge";
import { getVisibleNavItems, type NavItem } from "../../config/navigation";

interface BottomNavItem extends NavItem {
  iconSolid?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
}

// Icon solid mappings for primary nav items
const iconSolidMap: Record<string, React.ComponentType<React.SVGProps<SVGSVGElement>>> = {
  "/dashboard": HomeIconSolid,
  "/alerts": BellAlertIconSolid,
  "/inventory": CubeIconSolid,
  "/orders": ClipboardDocumentListIconSolid,
  "/purchases": ShoppingCartIconSolid,
  "/portal/products": ShoppingBagIconSolid,
};

/**
 * Primary nav items paths (shown as tabs on mobile).
 * These paths are always shown in primary navigation when visible to the user's role.
 * Other items (Investments, Settings, Users, Customers, Suppliers) appear in the "More" overflow menu.
 * Note: Items are still filtered by role via getVisibleNavItems() - this list just determines layout.
 */
const primaryNavPaths = ["/dashboard", "/alerts", "/inventory", "/portal/products", "/orders", "/purchases"];

export function BottomNav() {
  const { t } = useTranslation("common");
  const { user } = useAuthStore();
  const fetchAlertCount = useReorderAlertStore((s) => s.fetchAlertCount);
  const fetchToValidatePortalCount = useOrderStore((s) => s.fetchToValidatePortalCount);

  // Get visible nav items based on role
  const visibleNavItems = getVisibleNavItems(user?.role);
  
  // Filter and organize into primary and overflow items
  const { primaryNavItems, overflowItems } = useMemo(() => {
    const primary: BottomNavItem[] = [];
    const overflow: BottomNavItem[] = [];
    
    visibleNavItems.forEach((item) => {
      const navItem: BottomNavItem = {
        ...item,
        iconSolid: iconSolidMap[item.path],
      };
      
      if (primaryNavPaths.includes(item.path)) {
        primary.push(navItem);
      } else {
        // Only add to overflow if not already in primary (e.g., Settings, Investments, Users, Customers, Suppliers)
        overflow.push(navItem);
      }
    });
    
    return { primaryNavItems: primary, overflowItems: overflow };
  }, [visibleNavItems]);

  // Fetch alert count only for roles that can see Alerts (RBAC: admin, manager, staff)
  const canSeeAlerts = visibleNavItems.some((item) => item.path === "/alerts");
  const canSeeOrders = visibleNavItems.some((item) => item.path === "/orders");
  useEffect(() => {
    if (canSeeAlerts) fetchAlertCount();
  }, [canSeeAlerts, fetchAlertCount]);
  useEffect(() => {
    if (canSeeOrders) fetchToValidatePortalCount();
  }, [canSeeOrders, fetchToValidatePortalCount]);

  return (
    <nav className="md:hidden fixed bottom-0 inset-x-0 bg-white dark:bg-neutral-800 border-t border-neutral-200 dark:border-neutral-700 z-50 pb-safe">
      <div className="flex items-center justify-around h-16">
        {primaryNavItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            className={({ isActive }) =>
              `flex flex-col items-center justify-center min-h-touch min-w-touch px-2 py-1 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-neutral-800 ${
                isActive
                  ? "text-primary-600 dark:text-primary-400"
                  : "text-neutral-500 dark:text-neutral-400"
              }`
            }
          >
            {({ isActive }) => (
              <>
                <span className="relative flex items-center justify-center">
                  {isActive && item.iconSolid ? (
                    <item.iconSolid className="w-6 h-6" />
                  ) : (
                    <item.icon className="w-6 h-6" />
                  )}
                  {item.path === "/alerts" && (
                    <span className="absolute -top-1 -end-2">
                      <AlertBadge />
                    </span>
                  )}
                  {item.path === "/orders" && (
                    <span className="absolute -top-1 -end-2">
                      <OrdersBadge />
                    </span>
                  )}
                </span>
                <span className="text-xs mt-1 font-medium">{t(item.labelKey)}</span>
              </>
            )}
          </NavLink>
        ))}

        {/* More Menu - uses Headless UI's open state via render prop */}
        <Menu as="div" className="relative">
          {({ open }) => (
            <>
              <MenuButton
                className={`relative flex flex-col items-center justify-center min-h-touch min-w-touch px-2 py-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 rounded-lg ${
                  open
                    ? "text-primary-600 dark:text-primary-400"
                    : "text-neutral-500 dark:text-neutral-400"
                }`}
              >
                {open ? (
                  <EllipsisHorizontalIconSolid className="w-6 h-6" />
                ) : (
                  <EllipsisHorizontalIcon className="w-6 h-6" />
                )}
                <span className="text-xs mt-1 font-medium">{t("more")}</span>
              </MenuButton>

              <Transition
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <MenuItems className="absolute bottom-full end-0 mb-2 w-48 bg-white dark:bg-neutral-800 rounded-lg shadow-lg border border-neutral-200 dark:border-neutral-700 py-1 focus:outline-none">
                  {overflowItems.map((item) => (
                    <MenuItem key={item.path}>
                      {({ focus }) => (
                        <NavLink
                          to={item.path}
                          className={({ isActive }) =>
                            `flex items-center gap-3 px-4 py-3 ${
                              isActive
                                ? "text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20"
                                : focus
                                  ? "bg-neutral-100 dark:bg-neutral-700"
                                  : "text-neutral-700 dark:text-neutral-300"
                            }`
                          }
                        >
                          <item.icon className="w-5 h-5 flex-shrink-0" />
                          <span className="font-medium">{t(item.labelKey)}</span>
                        </NavLink>
                      )}
                    </MenuItem>
                  ))}
                </MenuItems>
              </Transition>
            </>
          )}
        </Menu>
      </div>
    </nav>
  );
}
